<template>
    <div class="data-form-input">

        <b-form-group :horizontal="horizontal"
                      v-if="field"
                      :label-cols="horizontal ? 6 : null"
                      :invalid-feedback="invalidFeedback(field)"
                      :valid-feedback="validFeedback(field)"
                      :description="$i18n.gt(field.description)">

            <slot name="label" v-if="field.type !== 'check'">
                <span class="required-marker" v-if="required">
                    *
                </span>
                {{ label }}
                <slot v-if="field.labelSlot"
                      name="labelSlot"
                      v-bind:field="field"/>
            </slot>

            <!-- radio group -->
            <b-form-radio-group v-if="field.type === 'radios'"
                                v-model="$v.form[fieldName].$model"
                                :options="options"
                                :state="state(field)">
            </b-form-radio-group>

            <!-- checkbox -->
            <b-form-checkbox v-else-if="field.type === 'check'"
                             v-model="$v.form[fieldName].$model"
                             value="1"
                             unchecked-value="0"
                             :state="state(field)">
                {{ label }}
            </b-form-checkbox>

            <!-- checkbox group -->
            <b-form-checkbox-group v-else-if="field.type === 'checks'"
                                   v-model="$v.form[fieldName].$model"
                                   :options="options"
                                   :state="state(field)">
            </b-form-checkbox-group>

            <!-- select -->
            <b-form-select v-else-if="field.type === 'select'"
                           :autocomplete="field.autocomplete"
                           v-model="$v.form[fieldName].$model"
                           :options="options"
                           :state="state(field)">
            </b-form-select>

            <!-- multiselect -->
            <template v-else-if="field.type === 'multiselect'">
                <data-form-input-multiselect :class="`t-${fieldName}`"
                                             v-model="$v.form[fieldName].$model"
                                             :$v="$v"
                                             :field-model="fieldModel"
                                             :field-name="fieldName"
                                             :model-instance="modelInstance"
                                             :state="state(field)"
                />
            </template>

            <!-- typeahead -->
            <data-form-input-typeahead v-else-if="field.type === 'typeahead'"
                                       :$v="$v"
                                       :fieldModel="fieldModel"
                                       :fieldName="fieldName"
                                       :model-instance="modelInstance">
            </data-form-input-typeahead>

            <!-- textarea -->
            <b-form-textarea v-else-if="field.type === 'textarea'"
                             :autocomplete="field.autocomplete"
                             v-model="$v.form[fieldName].$model"
                             debounce="500"
                             rows="3"
                             :state="state(field)"
                             :placeholder="$i18n.gt(field.placeholder)">
            </b-form-textarea>

            <!-- number -->
            <b-form-input v-else-if="field.type === 'number'"
                          :type="field.type"
                          :autocomplete="field.autocomplete"
                          :step="field.step"
                          v-model="$v.form[fieldName].$model"
                          debounce="500"
                          :state="state(field)">
            </b-form-input>

            <!-- password -->
            <div v-else-if="field.type === 'password'">
                <b-form-input :type="showPassword ? 'text' : 'password'"
                              :autocomplete="field.autocomplete"
                              v-model="$v.form[fieldName].$model"
                              debounce="500"
                              :state="state(field)">
                </b-form-input>
                <b-form-checkbox class="float-right mb-0"
                                 v-model="showPassword">
                    {{ $i18n.gt('LABEL_SHOW_PASSWORD') }}
                </b-form-checkbox>
            </div>

            <!-- custom -->
            <div v-else-if="field.type.substring(0, 3) === 'custom'">
                <b-form-input type="text"
                              :autocomplete="field.autocomplete"
                              v-model="$v.form[fieldName].$model"
                              debounce="500"
                              :state="state(field)">
                </b-form-input>
            </div>

            <!-- time -->
            <div v-else-if="field.type === 'time'">
                <b-form-timepicker v-model="$v.form[fieldName].$model"
                                   :disabled="field.disabled"
                                   :minutes-step="field.minutesStep"
                                   :seconds-step="field.secondsStep"
                                   :locale="field.locale"
                                   debounce="500">

                </b-form-timepicker>
            </div>

            <!-- other input type - text, date, tel, etc. -->
            <b-form-input v-else-if="isRegularInputType"
                          :type="field.type"
                          :disabled="field.disabled"
                          :autocomplete="field.autocomplete"
                          v-model="$v.form[fieldName].$model"
                          debounce="500"
                          :state="state(field)"
                          :placeholder="$i18n.gt(field.placeholder)">
            </b-form-input>

            <span v-else>
                <b-alert show variant="danger">
                    {{ $i18n.gt('LABEL_UNSUPPORTED_INPUT_TYPE') }}: {{ field.type }}
                </b-alert>
            </span>

        </b-form-group>

    </div>
</template>

<script>
/**
 * Generates a <b-form-group> with input type, translated label, validation state
 * and invalid/valid feedback based on a field model such as Participant.
 *
 * NB: text-based inputs have the "debounce" attribute added (courtesy of bootstrap-vue),
 * to make async validation much simpler (see e.g. email field in models/Participant.js).
 *
 * Valid feedback is used to display warnings that should not prevent form submission
 * (see e.g. email field in models/Practitioner.js).
 */
import Vue from 'vue'
import * as _ from 'lodash'

import {validationMixin} from 'vuelidate'

export default Vue.extend({
    name: 'DataFormInput',
    mixins: [validationMixin],
    props: {
        $v: Object, // vuelidate validation model
        fieldModel: Object,
        fieldName: String,
        modelInstance: Object,
        horizontal: Boolean
    },
    data() {
        return {
            showPassword: false,
            options: [],
            checksSelected: [], // for checkbox group
        }
    },
    computed: {
        field() {
            return this.fieldModel.fields[this.fieldName]
        },
        required() {
            return !!this.field.validations && this.field.validations.required
        },
        label() {
            // UPPER_CASE_STRINGS are translation keys (unless translateLabel is false)
            const fl = this.field.label
            const translate = !this.field.noLabelTranslation && _.toUpper(fl) == fl
            if (translate) {
                return this.$i18n.gt(fl)
            } else {
                return fl
            }
        },
        isRegularInputType() {
            return ['text', 'number', 'date', 'time', 'tel', 'email'].indexOf(this.field.type) != -1
        },
    },
    methods: {
        state(field) {
            const f = this.$v.form[this.fieldName]

            if (f) {
                return f.$dirty ? !f.$error : null
            } else {
                return null
            }
        },
        invalidFeedback(field) {
            let fb = ''
            _.each(_.keys(field.validations), (v) => {
                if (!fb && this.field.invalidFeedback && !this.$v.form[this.fieldName][v]) {
                    fb = this.$i18n.gt(this.field.invalidFeedback[v])
                }
            })
            return fb
        },
        validFeedback(field) {
            const warnings = this.$parent.warnings // parent = DataForm component
            // console.log(warnings)
            if (warnings) {
                if (_.toUpper(warnings[this.fieldName]) == warnings[this.fieldName]) {
                    return this.$i18n.gt(warnings[this.fieldName])
                } else {
                    return warnings[this.fieldName]
                }
            } else {
                return null
            }
        },
        populateOptions() {
            if (this.field.loadOptions) {
                this.field.loadOptions()
                    .then((options) => {
                        this.options = options
                    })
            } else if (this.field.options) {
                this.options = _.map(_.keys(this.field.options), (key) => {
                    // UPPER_CASE_STRINGS are translation keys
                    const label = this.field.options[key]
                    if (_.toUpper(label) == label) {
                        return {value: key, text: this.$i18n.gt(label)}
                    } else {
                        return {value: key, text: label}
                    }
                })
            }
            if (this.field.nullOptionLabel) {
                const label = this.field.nullOptionLabel
                if (_.toUpper(label) == label) {
                    this.options.splice(0, 0, {value: null, text: this.$i18n.gt(label)})
                } else {
                    this.options.splice(0, 0, {value: null, text: label})
                }
            }
        },
    },
    created() {
        if (!this.field) {
            console.error(`Can't create FormInput, missing field: ${this.fieldName}`)
            return
        }

        this.populateOptions()
    },
})
</script>

<style lang="scss">
.data-form-input {
    margin-top: 10px;

    .required-marker {
        font-weight: bold;
        font-size: 1.5em;
        color: red;
    }

    label {
        text-transform: none;
        font-size: 16px;
    }
}
</style>
